import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";
import { personneId } from "./personne.js";

/**
 * Timesheet : famille "temps de travail".
 *
 * Temps réellement enregistré par une personne sur un objet précis (projet,
 * tâche, intervention, client), facturable ou non, en saisie directe ou en
 * pointage entrée-sortie. Construite sur le modèle "famille + type" de la
 * bible (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète de la ligne de temps : il
 * connaît la famille générique `timesheet`. Le vocabulaire est porté par le
 * champ `type` (chaîne libre déclarée par l'app : "saisie", "pointage"...).
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma) :
 * `workerId` (la personne qui a enregistré le temps) est le CŒUR de la famille,
 * donc requis. L'objet visé est polymorphe (projet, tâche, ordre de travail ou
 * client) : chacun est un id optionnel, l'app en renseigne un selon le `type`.
 * `invoiceId` et `shiftId` (facture et créneau rattachés) sont optionnels.
 *
 * Sécurité : `hourlyRate` (taux horaire) est SENSIBLE et n'est pas dans ce
 * schéma v0 ; l'accès fin (chaque worker voit ses heures, les rôles manager et
 * facturation voient tout) viendra après v0. La famille reste org-wide, même
 * doctrine que asset.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille timesheet (hors champs standard du socle). */
export const timesheetCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "saisie", "pointage"). */
  type: z.string().min(1),
  /**
   * La PERSONNE qui a enregistré le temps : le `personId` d'un utilisateur de
   * l'org (annuaire `personnes`), jamais un e-mail ni une fiche `worker`. Celui
   * qui saisit SON temps est un utilisateur ; les `workerId` des familles de
   * planning, eux, désignent une ressource `workers`. Cœur de la famille, requis.
   */
  workerId: personneId(),
  /** Objet visé : projet sur lequel le temps est passé (référence `project`). Optionnel. */
  projectId: z.string().min(1).optional(),
  /** Objet visé : tâche (référence `task`). Optionnel. */
  taskId: z.string().min(1).optional(),
  /** Objet visé : intervention / ordre de travail (référence `work-order`). Optionnel. */
  workOrderId: z.string().min(1).optional(),
  /** Objet visé : client (référence `contact`). Optionnel. */
  contactId: z.string().min(1).optional(),
  /** Début du temps (saisie ou pointage d'entrée). Optionnel. */
  startAt: z.date().optional(),
  /** Durée réellement enregistrée, en minutes. Optionnel. */
  durationMinutes: z.number().int().optional(),
  /** Temps facturable au client ou non. Optionnel. */
  billable: z.boolean().optional(),
  /** Facture de rattachement si le temps a été facturé (référence `invoice`). Optionnel. */
  invoiceId: z.string().min(1).optional(),
  /** Créneau de planning rattaché (référence `shift`). Optionnel. */
  shiftId: z.string().min(1).optional(),
  // hourlyRate : sensible, ira dans un sous-document privé (cf. WIP), hors v0.
} as const;

/** Schéma complet de la famille timesheet (champs standard + champs métier). */
export const TimesheetSchema = z.object({
  ...baseEntityFields,
  ...timesheetCoreFields,
});

/** Type d'une timesheet. */
export type Timesheet = z.infer<typeof TimesheetSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const TimesheetCreateInputSchema = z.object({
  ...timesheetCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'une timesheet. */
export type TimesheetCreateInput = z.infer<typeof TimesheetCreateInputSchema>;

/**
 * Schema du patch de mise a jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont geres par le SDK : aucun des deux n'est accepte ici.
 */
export const TimesheetUpdateInputSchema = z
  .object({ ...timesheetCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial()
  /*
   * LE PROJET ET LA TACHE D'UNE SAISIE S'EFFACENT : `null` les retire du
   * document (le SDK pose `deleteField`). Une saisie passee d'une tache a un
   * projet seul doit perdre sa tache, sinon elle viserait une tache d'un
   * projet qu'elle ne nomme plus (retour du fondateur du 13/10/2026).
   */
  .extend({
    projectId: z.string().min(1).nullable().optional(),
    taskId: z.string().min(1).nullable().optional(),
  });

/** Type du patch de mise a jour d'une timesheet. */
export type TimesheetUpdateInput = z.infer<typeof TimesheetUpdateInputSchema>;
